import { z } from 'zod';

import type { WeekdayOption } from '@apple/utils/globalization/components/days-of-week/model';
import { weekdayOptionSchema } from '@apple/utils/globalization/components/days-of-week/model';
import {
	type CountryCode,
	type CurrencyCode,
	countryCodeSchema,
	currencyCodeSchema,
	localDateTimeSchema,
} from '@apple/utils/globalization/models';

import type { StoreId } from '@apple/features/store/models';

/** AppleRetail.Web.Features.Budgets.BudgetReportDetailsController.BudgetSummaryItem*/
export interface BudgetSummaryItem {
	periodNumber: number;
	budgetedAmount: string;
	amountSpent: string;
	difference: string;
	isOverBudget?: boolean;
}

/** AppleRetail.Web.Features.Budgets.BudgetReportDetailsController.BudgetDetailsItem*/
export interface BudgetDetailsItem {
	categoryId: number;
	categoryName: string;
	amountSpent: string;
	formattedPercentOfTotalBudget: string;
	percentOfTotalBudget: number;
}

/** AppleRetail.Web.Features.Budgets.BudgetReportDetailsController.ProductPurchaseDetailItem*/
export interface ProductPurchaseDetailItem {
	partNumber: string;
	description: string;
	quantity: number;
	formattedTotalCost: string;
}

/** AppleRetail.Web.Features.Stores.CurrentStoreStateController */
export interface StoreStateDetails {
	storeName: string;
	vintage: string | null;
	addressLine1: string;
	addressLine2: string;
	city: string;
	stateOrProvince: string;
	postalCode: string;
	countryCode: CountryCode;
	arvatoCustomerCode: string;
	thirdPartyId: string;
	fiscalYear: number;
	periodNumber: number;
	periodName: string;
	periodStartDate: Date | null | string;
	periodEndDate: Date | null | string;
	remainingBudget: number;
	allocatedAmount: number;
	formattedAllocatedAmount: string;
	consumedAmount: number;
	formattedRemainingBudget: string;
	formattedConsumedAmount: string;
	storeImageSmall: string;
	storeImageLarge: string;
	shipmentDays: WeekdayOption[];
	shipmentCutOffTime: number;
	storeUtcOffsetMinutes: number;
	currentShipmentDate: string;
	nextShipmentDate: string;
	pendingAmount: number;
	formattedPendingAmount: string;
	totalRemainingBudget?: number;
	formattedTotalRemainingBudget?: string;
	userCurrentDate: Date | null | string;
	currencyCode: CurrencyCode;
}

/** @see AppleRetail.Web.Features.Stores.BudgetODataController.BudgetListingModel */
export interface BudgetListingModel {
	storeId: StoreId;
	storeName: string;
	storeCode: string;
	countryCode: CountryCode;
	currencyCode: string;
	warehouseCode: string;
	fiscalYear: number;
	periodNumber: number;
	isOverBudget: boolean;
	periodName: string;
	allocatedAmount: number;
	transactionTotal: number;
	pendingTotal: number;
	remainingValue: number;
}

export type BudgetFilters = z.infer<typeof budgetFiltersSchema>;
export type BudgetTableData = BudgetListingModel & BudgetFilters;

/** AppleRetail.Web.Features.Budgets.BudgetReportDetailsController.BudgetSummaryItem*/
export const budgetSummaryItemSchema = z.object({
	periodNumber: z.number(),
	budgetedAmount: z.string(),
	amountSpent: z.string(),
	difference: z.string(),
	isOverBudget: z.boolean().optional(),
}) satisfies z.ZodType<BudgetSummaryItem>;

/** AppleRetail.Web.Features.Budgets.BudgetReportDetailsController.BudgetDetailsItem*/
export const budgetDetailsItemSchema = z.object({
	categoryId: z.number(),
	categoryName: z.string(),
	amountSpent: z.string(),
	formattedPercentOfTotalBudget: z.string(),
	percentOfTotalBudget: z.number(),
}) satisfies z.ZodType<BudgetDetailsItem>;

/** AppleRetail.Web.Features.Budgets.BudgetReportDetailsController.ProductPurchaseDetailItem*/
export const productPurchaseDetailItemSchema = z.object({
	partNumber: z.string(),
	description: z.string(),
	quantity: z.number(),
	formattedTotalCost: z.string(),
}) satisfies z.ZodType<ProductPurchaseDetailItem>;

/** AppleRetail.Web.Features.Stores.CurrentStoreStateController */
export const storeStateDetailsSchema = z.object({
	storeName: z.string(),
	vintage: z.string().nullable(),
	addressLine1: z.string(),
	addressLine2: z.string(),
	city: z.string(),
	stateOrProvince: z.string(),
	postalCode: z.string(),
	countryCode: countryCodeSchema,
	arvatoCustomerCode: z.string(),
	thirdPartyId: z.string(),
	fiscalYear: z.number(),
	periodNumber: z.number(),
	periodName: z.string(),
	periodStartDate: localDateTimeSchema.nullable(),
	periodEndDate: localDateTimeSchema.nullable(),
	remainingBudget: z.number(),
	allocatedAmount: z.number(),
	formattedAllocatedAmount: z.string(),
	consumedAmount: z.number(),
	formattedRemainingBudget: z.string(),
	formattedConsumedAmount: z.string(),
	storeImageSmall: z.string(),
	storeImageLarge: z.string(),
	shipmentDays: z.array(weekdayOptionSchema),
	shipmentCutOffTime: z.number(),
	storeUtcOffsetMinutes: z.number(),
	currentShipmentDate: z.string(),
	nextShipmentDate: z.string(),
	pendingAmount: z.number(),
	formattedPendingAmount: z.string(),
	totalRemainingBudget: z.number().optional(),
	formattedTotalRemainingBudget: z.string().optional(),
	userCurrentDate: localDateTimeSchema.nullable(),
	currencyCode: currencyCodeSchema,
}) satisfies z.ZodType<StoreStateDetails>;

/** @see AppleRetail.Web.Features.Stores.BudgetODataController.BudgetListingModel */
export const budgetListingModelSchema = z.object({
	storeId: z.string(),
	storeName: z.string(),
	storeCode: z.string(),
	countryCode: countryCodeSchema,
	currencyCode: currencyCodeSchema,
	warehouseCode: z.string(),
	fiscalYear: z.number(),
	periodNumber: z.number(),
	isOverBudget: z.boolean(),
	periodName: z.string(),
	allocatedAmount: z.number(),
	transactionTotal: z.number(),
	pendingTotal: z.number(),
	remainingValue: z.number(),
}) satisfies z.ZodType<BudgetListingModel>;

export const budgetFiltersSchema = z
	.object({
		storeIds: z.array(z.string()),
		countryCode: z.string(),
		warehouseCode: z.string(),
		fiscalYear: z.coerce.number(),
		periodNumber: z.coerce.number(),
		periodName: z.string(),
		isOverBudget: z.boolean(),
	})
	.partial();
